import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { BikeCard } from "../components/BikeCard";
import { ArrowRight, Wallet, Wrench, BadgeCheck, Clock, Zap, Tag, Phone, Instagram, FileCheck, ShieldAlert, CalendarDays } from "lucide-react";

export default function Home() {
  const [bikes, setBikes] = useState([]);

  useEffect(() => {
    api.get("/bikes").then((res) => setBikes(res.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center py-16 lg:py-24">
          <div className="animate-fade-up">
            <span className="label-caps text-primary">For food delivery drivers</span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]">
              Rent a bike.<br />Start earning <span className="text-primary">today.</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-md leading-relaxed">
              Weekly & monthly scooter hire for North West London delivery riders. In-house mechanics, top box & helmet included. CBT required · provisional accepted · 18+.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/bikes">
                <Button size="lg" data-testid="hero-browse-btn" className="bg-primary hover:bg-[#1D4ED8] text-white font-bold gap-2 h-12 px-7">
                  Browse Bikes <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/register">
                <Button size="lg" variant="outline" data-testid="hero-signup-btn" className="font-bold h-12 px-7 hover:text-primary hover:border-primary">
                  Become a Rider
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex gap-8">
              <div><p className="text-3xl font-black font-display">£80<span className="text-base text-muted-foreground">/wk</span></p><p className="label-caps mt-1">From</p></div>
              <div><p className="text-3xl font-black font-display">2 min</p><p className="label-caps mt-1">To book</p></div>
              <div><p className="text-3xl font-black font-display">24/7</p><p className="label-caps mt-1">Support</p></div>
            </div>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="absolute -inset-4 bg-primary/10 rounded-3xl -rotate-2" />
            <img
              src="https://customer-assets.emergentagent.com/job_quick-bike-share-1/artifacts/usaqkfdb_62D5CB82-EC92-4026-9E0D-19D57ACE78E0.PNG"
              alt="Honda PCX scooter from Dirt Or Road"
              className="relative rounded-2xl w-full h-[420px] object-cover shadow-xl"
            />
          </div>
        </div>
      </section>

      {/* Promo banner */}
      <section className="bg-primary text-primary-foreground" data-testid="promo-banner">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center">
          <span className="grid place-items-center w-8 h-8 rounded-full bg-white/20 shrink-0"><Tag className="w-4 h-4" /></span>
          <p className="font-bold text-base sm:text-lg">Hire for 4 consecutive weeks & get <span className="underline decoration-2 underline-offset-2">20% OFF</span> your 5th week hire.</p>
        </div>
      </section>

      {/* Value props */}
      <section id="drivers" className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-xl">Everything a delivery rider needs, nothing they don't.</h2>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Wallet, title: "Simple pricing", text: "Weekly & monthly hire only — no daily faff. Clear rates that fit your earnings." },
            { icon: Wrench, title: "In-house mechanics", text: "We service & repair all makes of bike. Servicing on your hire is included." },
            { icon: BadgeCheck, title: "Easy eligibility", text: "Provisional licence accepted, CBT required, 18+. Quick licence checks." },
            { icon: Clock, title: "Instant swap", text: "Bike trouble? Swap for another within hours, no questions asked." },
          ].map((f) => (
            <div key={f.title} className="p-6 border border-border rounded-xl hover:-translate-y-1 hover:shadow-lg transition-all duration-300 bg-white">
              <span className="grid place-items-center w-11 h-11 rounded-lg bg-primary/10 text-primary"><f.icon className="w-5 h-5" /></span>
              <h3 className="mt-4 text-lg font-bold">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-secondary border-y border-border">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <span className="label-caps text-primary">How it works</span>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">On the road in three steps</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-8">
            {[
              { n: "01", title: "Pick your scooter", text: "Browse our fleet of scooters & mopeds and compare clear weekly & monthly pricing." },
              { n: "02", title: "Book & pay online", text: "Choose weekly or monthly, confirm you meet the requirements and pay securely." },
              { n: "03", title: "Ride & deliver", text: "Collect your bike with helmet & top box and start your delivery shifts." },
            ].map((s) => (
              <div key={s.n} className="relative">
                <span className="text-5xl font-black font-display text-primary/20">{s.n}</span>
                <h3 className="mt-2 text-xl font-bold">{s.title}</h3>
                <p className="mt-2 text-muted-foreground leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section id="requirements" className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <span className="label-caps text-primary">Who can hire</span>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-xl">A few simple requirements</h2>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="requirements-grid">
          {[
            { icon: FileCheck, title: "Valid CBT", text: "A current CBT certificate is required to hire any of our scooters." },
            { icon: BadgeCheck, title: "Provisional accepted", text: "A provisional licence is fine — full licence not required to get going." },
            { icon: CalendarDays, title: "18+ & licence checks", text: "Minimum age 18. We carry out quick licence checks before every hire." },
            { icon: ShieldAlert, title: "Your own insurance", text: "Insurance is NOT included — you'll need to arrange your own cover." },
          ].map((r) => (
            <div key={r.title} className="p-6 border border-border rounded-xl bg-white">
              <span className="grid place-items-center w-11 h-11 rounded-lg bg-primary/10 text-primary"><r.icon className="w-5 h-5" /></span>
              <h3 className="mt-4 text-lg font-bold">{r.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{r.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Servicing & Repairs */}
      <section id="servicing" className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="label-caps text-primary">Servicing & repairs</span>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Mechanics you can trust</h2>
            <p className="mt-4 text-white/70 text-lg leading-relaxed">
              We're not just a hire company — we're mechanics. Our workshop services and repairs all makes and models of scooter and motorbike, whether you hire from us or not. MOT prep, tyres, brakes, servicing and breakdown fixes, done right.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="tel:07498253046" data-testid="servicing-call-btn">
                <Button size="lg" className="bg-primary hover:bg-[#1D4ED8] text-white font-bold gap-2 h-12 px-7">
                  <Phone className="w-4 h-4" /> Call 07498 253 046
                </Button>
              </a>
              <a href="https://instagram.com/dirtorroadhire" target="_blank" rel="noopener noreferrer" data-testid="servicing-instagram-btn">
                <Button size="lg" variant="outline" className="font-bold h-12 px-7 bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white gap-2">
                  <Instagram className="w-4 h-4" /> @dirtorroadhire
                </Button>
              </a>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {["Full servicing", "Tyres & brakes", "MOT prep", "Breakdown repairs", "Diagnostics", "All makes & models"].map((s) => (
              <div key={s} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 p-4">
                <Wrench className="w-4 h-4 text-primary shrink-0" />
                <span className="font-semibold text-sm">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured bikes */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="label-caps text-primary">The fleet</span>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Popular with drivers</h2>
          </div>
          <Link to="/bikes" className="hidden sm:flex items-center gap-2 font-bold text-primary hover:gap-3 transition-all">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {bikes.map((b) => <BikeCard key={b.id} bike={b} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20">
        <div className="relative overflow-hidden rounded-3xl bg-[#0A0A0A] text-white p-10 sm:p-16">
          <div className="absolute right-0 top-0 w-72 h-72 bg-primary/30 blur-3xl rounded-full" />
          <div className="relative max-w-xl">
            <Zap className="w-10 h-10 text-primary" />
            <h2 className="mt-4 text-3xl sm:text-4xl font-black tracking-tight">Ready to start your next shift?</h2>
            <p className="mt-4 text-white/70 text-lg">Join London's delivery riders who trust Dirt Or Road to keep them earning.</p>
            <Link to="/register">
              <Button size="lg" data-testid="cta-signup-btn" className="mt-8 bg-primary hover:bg-[#1D4ED8] text-white font-bold h-12 px-8 gap-2">
                Get Started Free <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
